import { useEffect, useState } from "react";
import {
  Grid,
  Switch,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  Box,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { createStyles, makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  GetVMExtraSettingConfigRequestAction,
  UpdateVMExtraSettingConfigRequestAction,
} from "../../../../../store/Employee/Visitors/Action";

const VisitorsCheckinFormConfig = () => {
  const useStyles = makeStyles((theme) =>
    createStyles({
      Paper: {
        boxShadow:
          theme.palette.mode === "dark"
            ? "rgb(0 0 0 / 20%) 0px 0px 2px 0px, rgb(0 0 0 / 12%) 0px 12px 24px -4px"
            : "0px 3px 3px -2px #cbc3c3, 0px 3px 4px 0px rgb(242 242 242), 0px 1px 8px 0px rgb(242 242 242)",
        padding: "20px",
        borderRadius: "10px",
        backgroundColor: "#f3edfd",
        position: "relative",
      },
      Backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        position: "absolute", 
        borderRadius: "inherit", 
      },
    })
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const { visitorsExtraSettingData, visitorsSettingLoading } = useSelector(
    (state) => state?.EmployeeReducer?.VisitorsReducer
  );
  const [config, setConfig] = useState({
    vehicleNumber: false,
    idProof: false,
    is_canteen_token_applicable: false,
    is_inout_qr_applicable: false,
    employee_approval_required: false,
  });

  const groups = [
    {
      title: "Check-In Form",
      description: "Enabled fields will be asked during visitors check-in form.",
      fields: [
        {
          label: "Vehicle Number",
          key: "vehicleNumber",
          description: "Ask visitors to enter their vehicle number.",
        },
        {
          label: "ID Proof",
          key: "idProof",
          description: "Require visitors to provide identification details during check-in.",
        },
      ],
    },
    {
      title: "Other Controls",
      description: "Additional visitor flow controls.",
      fields: [
        {
          label: "Canteen Token",
          key: "is_canteen_token_applicable",
          description: "Issue a token for canteen access when enabled.",
        },
        {
          label: "Visitor Approval",
          key: "employee_approval_required",
          description: "Send an approval request to the host employee after visitor check-in. QR access will be shared with the visitor once approved.",
        },
        {
          label: "QR In-Out",
          key: "is_inout_qr_applicable",
          description: "Enable QR-based visitor check-in and check-out tracking.",
        },
      ],
    },
  ];

  const handleToggleSwitch = (newState, control_type) => {
    // setConfig((prev) => ({ ...prev, [control_type]: newState }));

    const payload = {
      vehicle_no:
        control_type === "vehicleNumber"
          ? newState
          : config?.vehicleNumber,
      id_proof:
        control_type === "idProof"
          ? newState
          : config?.idProof,
      is_canteen_token_applicable:
        control_type === "is_canteen_token_applicable"
          ? newState
          : config?.is_canteen_token_applicable,
      is_inout_qr_applicable:
        control_type === "is_inout_qr_applicable"
          ? newState
          : config?.is_inout_qr_applicable,
      employee_approval_required:
        control_type === "employee_approval_required"
          ? newState
          : config?.employee_approval_required,
    };

    dispatch(
      UpdateVMExtraSettingConfigRequestAction({ ...payload, control_type })
    );
  };
  useEffect(() => {
    setConfig({
      vehicleNumber: visitorsExtraSettingData?.[0]?.vehicle_no,
      idProof: visitorsExtraSettingData?.[0]?.id_proof,
      is_canteen_token_applicable:
        visitorsExtraSettingData?.[0]?.is_canteen_token_applicable,
      is_inout_qr_applicable:
        visitorsExtraSettingData?.[0]?.is_inout_qr_applicable,
      employee_approval_required:
        visitorsExtraSettingData?.[0]?.employee_approval_required,
    });
  }, [visitorsExtraSettingData]);
  useEffect(() => {
    dispatch(GetVMExtraSettingConfigRequestAction());
  }, [dispatch]);
  return (
    <Paper
      className={classes.Paper}
      sx={{
        borderRadius: "16px",
        width: "100%",
      }}
    >
      <Backdrop
        className={classes.Backdrop}
        open={visitorsSettingLoading} 
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ p: 1, py: 0 }}>
        {groups?.map((group) => (
          <Box key={group.title} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {group.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {group.description}
            </Typography>
            <Grid container spacing={1}>
              {group?.fields?.map((field) => (
                <Grid
                  item
                  xs={6}
                  sm={3.5}
                  key={field.key}
                  sx={{ display: "flex", alignItems: "center", pr: 2, pb: 1, border: "1px solid #E0E0E0", borderRadius: 1, mr: 1.5 }}
                >
                  <Box sx={{ flexGrow: 1, pr: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography color={"inherit"} variant="body1">
                        {field.label}
                      </Typography>
                      <Tooltip title={field?.description} arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer", mb: 0.2 }} />
                      </Tooltip>
                    </Box>
                  </Box>
                  <Switch
                    checked={config[field.key]}
                    onChange={() => handleToggleSwitch(!config[field.key], field.key)}
                    inputProps={{ "aria-label": `Switch for ${field.label}` }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default VisitorsCheckinFormConfig;